import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { useHybridCart } from "@/contexts/HybridCartContext";
import {
  Loader2,
  CreditCard,
  Banknote,
  Truck,
  MessageCircle,
} from "lucide-react";
import { isEqual } from "date-fns";
import api from "@/services/api"; // Import the configured api instance
import { logger, addBreadcrumb } from "@/lib/sentry";

// Dynamic schema function to make shipping optional for digital-only orders
const createGuestCheckoutSchema = (hasPhysicalProducts: boolean) =>
  z.object({
    // Guest Info
    fullName: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
    email: z.string().email("Email inválido"),
    phone: z.string().min(8, "El teléfono debe tener al menos 8 dígitos"),

    // Dirección: obligatoria cuando el carrito trae productos físicos.
    street: hasPhysicalProducts
      ? z.string().min(1, "Ingresa tu dirección")
      : z.string().optional(),
    district: hasPhysicalProducts
      ? z.string().min(1, "Selecciona el distrito")
      : z.string().optional(),
    corregimiento: hasPhysicalProducts
      ? z.string().min(1, "Selecciona el corregimiento")
      : z.string().optional(),
    province: hasPhysicalProducts
      ? z.string().min(1, "Selecciona la provincia")
      : z.string().optional(),
    postalCode: hasPhysicalProducts
      ? z.string().regex(/^\d{5}$/, "El código postal debe tener 5 dígitos")
      : z.string().optional(),
    country: z.string().default("Panamá"),

    // Payment & Notes
    paymentMethod: z.enum([
      "whatsapp",
      "paguelofacil",
      "cubopago",
      "bank_transfer",
      "cash_on_delivery",
    ]),
    notes: z.string().optional(),
  });

type GuestCheckoutFormData = z.infer<
  ReturnType<typeof createGuestCheckoutSchema>
>;

interface GuestCheckoutFormProps {
  onBack: () => void;
  onSuccess: (orderData: Record<string, unknown>) => void;
  onMethodChange?: (method: string) => void;
  onSubStepChange?: (info: {
    name: string;
    index: number;
    total: number;
  }) => void;
  active?: boolean;
}

const paymentMethods: Array<{
  value: GuestCheckoutFormData["paymentMethod"];
  label: string;
  description: string;
  icon: typeof MessageCircle;
  recommended?: boolean;
  disabled?: boolean;
}> = [
  {
    value: "whatsapp",
    label: "Pago mediante WhatsApp",
    description:
      "Serás atendido por WhatsApp para pagar con Visa, Mastercard o Yappy",
    icon: MessageCircle,
    recommended: false,
    disabled: false,
  },
  {
    value: "cubopago",
    label: "Tarjeta de crédito/débito",
    description: "Paga con Visa o Mastercard",
    icon: CreditCard,
    recommended: true,
    disabled: false,
  },
  {
    value: "bank_transfer",
    label: "Transferencia bancaria",
    description: "Próximamente disponible",
    icon: Banknote,
    disabled: true,
  },
  {
    value: "cash_on_delivery",
    label: "Pago contra entrega",
    description: "Próximamente disponible",
    icon: Truck,
    disabled: true,
  },
];

// Datos de provincias disponibles para entrega (solo Panamá y Panamá Oeste)
const availableProvinces = [
  { value: "panama", label: "Panamá" },
  { value: "panama_oeste", label: "Panamá Oeste" },
];

// Datos de distritos y corregimientos organizados por provincia
const locationData = {
  panama: {
    districts: [
      { value: "balboa", label: "Balboa" },
      { value: "chepo", label: "Chepo" },
      { value: "chiman", label: "Chimán" },
      { value: "panama", label: "Panamá" },
      { value: "san_miguelito", label: "San Miguelito" },
      { value: "taboga", label: "Taboga" },
    ],
    corregimientos: {
      balboa: [
        "La Ensenada",
        "La Esmeralda",
        "La Guinea",
        "Pedro González",
        "Saboga",
        "San Miguel",
      ],
      chepo: [
        "Cañita",
        "Chepillo",
        "El Llano",
        "Las Margaritas",
        "Chepo",
        "Santa Cruz de Chinina",
        "Tortí",
        "Madugandí",
      ],
      chiman: [
        "Brujas",
        "Chimán",
        "Gonzalo Vásquez",
        "Pásiga",
        "Unión Santeña",
      ],
      panama: [
        "24 de Diciembre",
        "Alcalde Díaz",
        "Ancón",
        "Betania",
        "Bella Vista",
        "Calidonia",
        "Caimitillo",
        "Chilibre",
        "Curundú",
        "Don Bosco",
        "El Chorrillo",
        "Ernesto Córdoba Campos",
        "Juan Díaz",
        "Las Cumbres",
        "Las Garzas",
        "Las Mañanitas",
        "Pacora",
        "Parque Lefevre",
        "Pedregal",
        "Pueblo Nuevo",
        "Río Abajo",
        "San Felipe",
        "San Francisco",
        "San Martín",
        "Santa Ana",
        "Tocumen",
      ],
      san_miguelito: [
        "Amelia Denis de Icaza",
        "Arnulfo Arias",
        "Belisario Frías",
        "Belisario Porras",
        "José Domingo Espinar",
        "Mateo Iturralde",
        "Omar Torrijos",
        "Rufina Alfaro",
        "Victoriano Lorenzo",
      ],
      taboga: ["Otoque Occidente", "Otoque Oriente", "Taboga"],
    },
  },
  panama_oeste: {
    districts: [
      { value: "arraijan", label: "Arraiján" },
      { value: "capira", label: "Capira" },
      { value: "chame", label: "Chame" },
      { value: "la_chorrera", label: "La Chorrera" },
      { value: "san_carlos", label: "San Carlos" },
    ],
    corregimientos: {
      arraijan: [
        "Arraiján",
        "Burunga",
        "Cerro Silvestre",
        "Juan Demóstenes Arosemena",
        "Nuevo Emperador",
        "Santa Clara",
        "Vacamonte",
        "Veracruz",
        "Vista Alegre",
      ],
      capira: [
        "Capira",
        "Caimito",
        "Campana",
        "Cermeño",
        "Cirí de Los Sotos",
        "Cirí Grande",
        "El Cacao",
        "La Trinidad",
        "Las Ollas Arriba",
        "Lídice",
        "Santa Rosa",
        "Villa Carmen",
        "Villa Rosario",
      ],
      chame: [
        "Chame",
        "Bejuco",
        "Buenos Aires",
        "Cabuya",
        "Chicá",
        "El Líbano",
        "Las Lajas",
        "Nueva Gorgona",
        "Punta Chame",
        "Sajalices",
        "Sorá",
      ],
      la_chorrera: [
        "La Chorrera",
        "Barrio Balboa",
        "Barrio Colón",
        "Amador",
        "Arosemena",
        "El Arado",
        "El Coco",
        "Feuillet",
        "Guadalupe",
        "Herrera",
        "Hurtado",
        "Iturralde",
        "La Represa",
        "Los Díaz",
        "Mendoza",
        "Obaldía",
        "Playa Leona",
        "Puerto Caimito",
        "Santa Rita",
      ],
      san_carlos: [
        "San Carlos",
        "El Espino",
        "El Higo",
        "Guayabito",
        "La Ermita",
        "La Laguna",
        "Las Uvas",
        "Los Llanitos",
        "San José",
      ],
    },
  },
};

export const KYCCheckoutForm = ({
  onBack,
  onSuccess,
  onMethodChange,
  onSubStepChange,
  active = true,
}: GuestCheckoutFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [subStep, setSubStep] = useState(0);
  const [selectedProvince, setSelectedProvince] = useState<string>("");
  const [selectedDistrict, setSelectedDistrict] = useState<string>("");
  const [availableDistricts, setAvailableDistricts] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [availableCorregimientos, setAvailableCorregimientos] = useState<
    string[]
  >([]);

  const { cart, getCartItems } = useHybridCart();
  const { toast } = useToast();

  // The cart from the context is now fully normalized.
  // We can use its values directly.
  const cartItems = getCartItems();
  const subtotal = cart?.subtotal || 0;

  const hasPhysicalProducts = cartItems.some((i) =>
    "type" in i && i.type ? i.type === "físico" : true
  );

  const subSteps = hasPhysicalProducts
    ? ["contacto", "envio", "pago-notas"]
    : ["contacto", "pago-notas"];
  const currentSubStep = subSteps[Math.min(subStep, subSteps.length - 1)];
  const SUB_STEP_LABELS: Record<string, string> = {
    contacto: "Datos de contacto",
    envio: "Dirección de envío",
    "pago-notas": "Pago y notas",
  };
  const stepFields: Record<string, Array<keyof GuestCheckoutFormData>> = {
    contacto: ["fullName", "email", "phone"],
    envio: ["street", "province", "district", "corregimiento", "postalCode"],
    "pago-notas": ["paymentMethod"],
  };

  // 1️⃣ Only recreate the schema when this flag changes
  const guestCheckoutSchema = useMemo(
    () => createGuestCheckoutSchema(hasPhysicalProducts),
    [hasPhysicalProducts]
  );

  // 2️⃣ Give RHF a `key` so it remounts when the schema truly changes
  const form = useForm<GuestCheckoutFormData>({
    key: hasPhysicalProducts ? "physical" : "digital",
    resolver: zodResolver(guestCheckoutSchema),
    defaultValues: {
      country: "Panamá",
      paymentMethod: "cubopago",
    },
  });

  // 3️⃣ Destructure as usual
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = form;

  const selectedPaymentMethod = watch("paymentMethod");

  // Notifica al padre el método elegido para que el stepper ajuste sus pasos.
  useEffect(() => {
    onMethodChange?.(selectedPaymentMethod);
  }, [selectedPaymentMethod, onMethodChange]);

  // Reporta el sub-paso actual para que el indicador del padre refleje la fase real
  // (p. ej. "Pago" activo mientras se elige el método, no recién en la tarjeta).
  useEffect(() => {
    onSubStepChange?.({
      name: currentSubStep,
      index: subStep,
      total: subSteps.length,
    });
  }, [subStep, currentSubStep, subSteps.length, onSubStepChange]);

  // Cleanup effect para prevenir actualizaciones en componentes desmontados
  useEffect(() => {
    let isMounted = true;

    return () => {
      isMounted = false;
    };
  }, []);

  // Handle province selection
  const handleProvinceChange = (provinceValue: string) => {
    setSelectedProvince(provinceValue);
    setSelectedDistrict("");
    setAvailableCorregimientos([]);

    // Update available districts
    const provinceData =
      locationData[provinceValue as keyof typeof locationData];
    if (provinceData) {
      setAvailableDistricts(provinceData.districts);
    } else {
      setAvailableDistricts([]);
    }

    // Update form values
    setValue(
      "province",
      availableProvinces.find((p) => p.value === provinceValue)?.label || ""
    );
    setValue("district", "");
    setValue("corregimiento", "");
  };

  // Handle district selection
  const handleDistrictChange = (districtValue: string) => {
    setSelectedDistrict(districtValue);

    // Update available corregimientos
    const provinceData =
      locationData[selectedProvince as keyof typeof locationData];
    if (provinceData) {
      const corregimientos =
        provinceData.corregimientos[
          districtValue as keyof typeof provinceData.corregimientos
        ];
      setAvailableCorregimientos(corregimientos || []);
    }

    // Update form values
    setValue(
      "district",
      availableDistricts.find((d) => d.value === districtValue)?.label || ""
    );
    setValue("corregimiento", "");
  };

  const goToNextSubStep = async () => {
    const valid = await trigger(stepFields[currentSubStep]);
    if (valid) setSubStep((s) => Math.min(s + 1, subSteps.length - 1));
  };

  const handleBack = () => {
    if (subStep > 0) {
      setSubStep((s) => Math.max(s - 1, 0));
    } else {
      onBack();
    }
  };

  const onSubmit = async (data /* data: GuestCheckoutFormData */) => {
    // Form validation is handled by zodResolver
    setIsLoading(true);

    // CRÍTICO: Si es WhatsApp, abrir la ventana INMEDIATAMENTE antes de cualquier await
    // para evitar que el navegador bloquee el popup
    let whatsappWindow: Window | null = null;
    if (data.paymentMethod === "whatsapp") {
      whatsappWindow = window.open("about:blank", "_blank");

      if (whatsappWindow) {
        // Mostrar mensaje de carga en la ventana
        whatsappWindow.document.write(`
          <html>
            <head>
              <title>Redirigiendo a WhatsApp...</title>
              <style>
                body {
                  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  height: 100vh;
                  margin: 0;
                  background: #f0f2f5;
                }
                .loader {
                  text-align: center;
                }
                .spinner {
                  border: 4px solid #f3f3f3;
                  border-top: 4px solid #25D366;
                  border-radius: 50%;
                  width: 40px;
                  height: 40px;
                  animation: spin 1s linear infinite;
                  margin: 0 auto 20px;
                }
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              </style>
            </head>
            <body>
              <div class="loader">
                <div class="spinner"></div>
                <p>Redirigiendo a WhatsApp...</p>
              </div>
            </body>
          </html>
        `);
      } else {
        toast({
          title: "Popup bloqueado",
          description:
            "Por favor permite ventanas emergentes para abrir WhatsApp",
          variant: "destructive",
        });
      }
    }

    try {
      // Debug: log cart items structure
      console.log("🔍 Cart items:", cartItems);
      console.log("🔍 First item:", cartItems[0]);

      // Prepare cart items for backend
      const backendCartItems = cartItems.map((item) => {
        // Handle both guest and user cart item formats
        let productId;
        let productImage;
        let productName;
        let productPrice;

        if ("productId" in item) {
          // Guest cart format
          productId = item.productId;
          productImage = item.image;
          productName = item.name;
          productPrice = item.price;
        } else if ("product" in item && item.product) {
          // User cart format
          productId = item.product._id;
          productImage = item.product.image;
          productName = item.product.name || item.product.title;
          productPrice = item.product.price;
        }

        console.log("📦 Item:", productName, "→ ProductID:", productId);
        console.log("⭐ Customization:", item.customization);

        // Detectar si es una agenda personalizada
        const isAgendaPersonalizada =
          productId && productId.startsWith("agenda-personalizada-");

        const cartItem: {
          productId: string;
          quantity: number;
          name?: string;
          price?: number;
          image?: string;
          customization?: { personalizedName?: string; notes?: string };
          agendaConfig?: any;
        } = {
          productId,
          quantity: item.quantity,
        };

        // Incluir datos básicos para el backend
        if (productName) cartItem.name = productName;
        if (productPrice) cartItem.price = productPrice;

        // Incluir personalización si existe
        if (item.customization) {
          cartItem.customization = item.customization;
        }

        // Si es agenda personalizada, agregar la configuración completa y la imagen
        if (isAgendaPersonalizada) {
          // Incluir la imagen del preview
          if (productImage) {
            cartItem.image = productImage;
          }

          try {
            const configKey = `agenda-config-${productId}`;
            const storedConfig = localStorage.getItem(configKey);

            if (storedConfig) {
              const agendaConfig = JSON.parse(storedConfig);
              cartItem.agendaConfig = agendaConfig;

              console.log("📔 Agenda personalizada detectada:", {
                productId,
                hasConfig: true,
                hasImage: !!productImage,
                totalPaginas: agendaConfig.totalPaginas,
                secciones: agendaConfig.seccionesPersonalizadas?.length,
              });
            } else {
              console.warn(
                "⚠️ No se encontró configuración de agenda en localStorage para:",
                configKey
              );
            }
          } catch (error) {
            console.error(
              "❌ Error al recuperar configuración de agenda:",
              error
            );
          }
        }

        return cartItem;
      });

      // Prepare checkout data for guest endpoint (works for everyone)
      const checkoutData = {
        guestInfo: {
          fullName: data.fullName,
          email: data.email,
          phone: data.phone,
        },
        ...(hasPhysicalProducts
          ? {
              shippingAddress: {
                street: data.street,
                district: data.district,
                corregimiento: data.corregimiento,
                province: data.province,
                postalCode: data.postalCode,
                country: data.country || "Panamá",
              },
            }
          : {}),
        cartItems: backendCartItems,
        paymentMethod: data.paymentMethod,
        // Only include the notes field if it has a value.
        ...(data.notes && { notes: data.notes }),
      };

      console.log({ checkoutData });
      /* console.log(
        "📦 Backend cart items que voy a enviar:",
        JSON.stringify(backendCartItems, null, 2)
      ); */

      // Use only guest endpoint for everyone (no authentication required)
      // Tarjeta (cubopago): endpoint síncrono, esperamos la respuesta real con orderId.
      // WhatsApp: endpoint con respuesta optimista + timeout para no bloquear la redirección.
      const isCardPayment = data.paymentMethod === "cubopago";
      const apiCall = isCardPayment
        ? api.post("/checkout/guest/initiate-card", checkoutData)
        : api.post("/checkout/guest/initiate", checkoutData);

      // Timeout optimista: después de 5 segundos, continuar con la redirección
      const OPTIMISTIC_TIMEOUT = 5000; // 5 segundos max de espera

      const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => {
          console.log('⏱️ Timeout optimista alcanzado - continuando con redirección');
          resolve({
            isTimeout: true,
            status: 200,
            data: {
              data: {
                order: {
                  orderNumber: `TEMP-${Date.now()}`,
                  total: subtotal || 0,
                  status: 'pending',
                  paymentMethod: data.paymentMethod
                }
              }
            }
          });
        }, OPTIMISTIC_TIMEOUT);
      });

      // Race entre la llamada API y el timeout (solo para WhatsApp)
      const response: any = isCardPayment
        ? await apiCall
        : await Promise.race([apiCall, timeoutPromise]);

      // Si es timeout, la llamada API continúa en background
      if (response.isTimeout) {
        console.log('⚡ Usando respuesta optimista, API procesando en background');
        // La redirección a WhatsApp continuará con datos temporales
      }

      if (response.status !== 200 && response.status !== 201) {
        throw new Error(response.data.error || "Error al iniciar el checkout");
      }

      const result = response.data;

      console.log("Checkout result:", result);
      console.log("Payment method:", data.paymentMethod);

      // Si el método de pago es WhatsApp, redirigir
      if (data.paymentMethod === "whatsapp") {
        const phone = "50761568575";

        // Crear mensaje con detalles de la orden
        let message = `Hola, quiero confirmar mi pedido:\n\n`;
        message += `*Orden #${result.data.order.orderNumber}*\n\n`;
        message += `*Productos:*\n`;

        cartItems.forEach((item, index) => {
          // Extraer productId dependiendo del formato (guest vs user)
          const productId =
            "productId" in item ? item.productId : item.product?._id;
          const isAgendaPersonalizada =
            productId && productId.startsWith("agenda-personalizada-");

          message += `${index + 1}. ${item.name} x${item.quantity} - USD ${(
            item.subtotal || 0
          ).toFixed(2)}\n`;

          // Si es agenda personalizada, mostrar configuración detallada
          if (isAgendaPersonalizada) {
            try {
              const configKey = `agenda-config-${productId}`;
              const storedConfig = localStorage.getItem(configKey);

              if (storedConfig) {
                const agendaConfig = JSON.parse(storedConfig);

                message += `\n   *CONFIGURACION DE TU AGENDA:*\n`;

                // Tablero borrable
                if (agendaConfig.tableroBorrable) {
                  const tableroLabels: Record<string, string> = {
                    semana: "Semana",
                    ideas: "Ideas / Bocetos",
                  };
                  // Manejar tanto string como objeto
                  const tableroValue =
                    typeof agendaConfig.tableroBorrable === "string"
                      ? agendaConfig.tableroBorrable
                      : agendaConfig.tableroBorrable?.type || "";
                  message += `   > Tablero: ${
                    tableroLabels[tableroValue] || tableroValue
                  }\n`;
                }

                // Secciones personalizadas
                if (agendaConfig.seccionesPersonalizadas?.length > 0) {
                  message += `   > Secciones (${
                    agendaConfig.seccionesPersonalizadas.length
                  }): ${agendaConfig.seccionesPersonalizadas
                    .map((s: any) => s.nombre)
                    .join(", ")}\n`;
                }

                // Personalización visual
                if (agendaConfig.personalizacionVisual) {
                  const visual = agendaConfig.personalizacionVisual;
                  if (visual.tipo === "logo") {
                    message += `   > Portada: Logo personalizado (Color: ${
                      visual.color || "N/A"
                    })\n`;
                  } else if (visual.tipo === "estampado") {
                    message += `   > Portada: Estampado "${visual.estampado}"\n`;
                  }
                }

                // QR Codes
                const qrCodes = [];
                if (agendaConfig.qrCodes?.instagram) qrCodes.push("Instagram");
                if (agendaConfig.qrCodes?.whatsapp) qrCodes.push("WhatsApp");
                if (qrCodes.length > 0) {
                  message += `   > Codigos QR: ${qrCodes.join(", ")}\n`;
                }

                // Total de páginas
                if (agendaConfig.totalPaginas) {
                  message += `   > Total: ${agendaConfig.totalPaginas} paginas\n`;
                }

                message += `\n`;
              }
            } catch (error) {
              console.error(
                "Error al obtener config de agenda para WhatsApp:",
                error
              );
              // Si falla, mostrar solo las notas de customization
              if (item.customization?.notes) {
                message += `   Notas: ${item.customization.notes}\n`;
              }
            }
          } else {
            // Productos normales - mostrar personalización estándar
            if (item.customization?.personalizedName) {
              message += `   *Personalizar con:* "${item.customization.personalizedName}"\n`;
            }
            if (item.customization?.notes) {
              message += `   Notas: ${item.customization.notes}\n`;
            }
          }
        });

        message += `\n*Total: USD ${(subtotal || 0).toFixed(2)}*\n\n`;
        message += `*Datos de contacto:*\n`;
        message += `Nombre: ${data.fullName}\n`;
        message += `Email: ${data.email}\n`;
        message += `Telefono: ${data.phone}\n`;

        // Solo incluir dirección si fue proporcionada
        if (hasPhysicalProducts && data.street && data.district) {
          message += `\n*Direccion de envio:*\n`;
          message += `${data.street}\n`;
          message += `${data.corregimiento}, ${data.district}\n`;
          message += `${data.province}, Panama\n`;
        } else if (hasPhysicalProducts) {
          message += `\n*Direccion:* Por coordinar\n`;
        }

        if (data.notes) {
          message += `\n*Notas:* ${data.notes}\n`;
        }

        const waLink = `https://wa.me/${phone}?text=${encodeURIComponent(
          message
        )}`;

        console.log("WhatsApp link:", waLink);

        // Redirigir la ventana que abrimos al inicio
        try {
          if (whatsappWindow && !whatsappWindow.closed) {
            whatsappWindow.location.href = waLink;
            console.log("WhatsApp window redirected successfully");
          } else {
            console.warn("WhatsApp window was closed or blocked");
            // Fallback: intentar abrir de nuevo
            window.open(waLink, "_blank");
          }
        } catch (redirectError) {
          console.error("Error redirecting to WhatsApp:", redirectError);
          // Último intento: abrir directamente
          try {
            window.open(waLink, "_blank");
          } catch (finalError) {
            console.error("Failed to open WhatsApp link:", finalError);
          }
        }
      }

      toast({
        title: "¡Pedido creado exitosamente!",
        description: `Tu orden #${
          result.data.order.orderNumber
        } ha sido creada. ${
          data.paymentMethod === "whatsapp"
            ? "Serás redirigido a WhatsApp para confirmar tu pago."
            : ""
        }`,
      });

      // Usar setTimeout para evitar problemas de sincronización del DOM
      setTimeout(() => {
        try {
          onSuccess({ ...(result.data ?? {}), guestEmail: data.email });
        } catch (callbackError) {
          console.error("Error en callback onSuccess:", callbackError);
        }
      }, 100);
    } catch (error: unknown) {
      console.error("❌ Checkout error:", error);

      // Si hay error, cerrar la ventana de WhatsApp si está abierta
      if (whatsappWindow && !whatsappWindow.closed) {
        try {
          whatsappWindow.close();
        } catch (closeError) {
          console.warn("No se pudo cerrar ventana WhatsApp:", closeError);
        }
      }

      // Extraer información detallada del error
      let errorMessage = "Hubo un problema al procesar tu pedido";
      let errorDetails = "";

      if (error && typeof error === "object") {
        // Error de Axios con response del backend
        if ("response" in error && error.response && typeof error.response === "object") {
          const response = error.response as any;
          console.error("📋 Error response:", response.data);

          // Extraer mensaje específico del backend
          if (response.data?.error) {
            errorMessage = response.data.error;
          } else if (response.data?.message) {
            errorMessage = response.data.message;
          }

          // Agregar detalles de validación si existen
          if (response.data?.details) {
            errorDetails = Array.isArray(response.data.details)
              ? response.data.details.join(", ")
              : String(response.data.details);
            console.error("🔍 Validation details:", errorDetails);
          }

          // Log status code
          console.error(`🚨 Status: ${response.status || "unknown"}`);

          // 🆕 ENVIAR A SENTRY (en producción)
          logger.error("Checkout API Error", error, {
            tags: {
              operation: 'checkout',
              status: response.status?.toString() || 'unknown',
              errorType: response.data?.error || 'unknown'
            },
            extra: {
              errorMessage,
              errorDetails,
              responseData: response.data,
              cartItemsCount: cartItems?.length || 0,
              paymentMethod: data.paymentMethod
            }
          });
        } else if ("message" in error) {
          errorMessage = String(error.message);

          // 🆕 ENVIAR A SENTRY (en producción)
          logger.error("Checkout Generic Error", error, {
            tags: {
              operation: 'checkout',
              errorType: 'generic'
            },
            extra: {
              errorMessage,
              paymentMethod: data.paymentMethod
            }
          });
        }
      }

      toast({
        title: "Error en el checkout",
        description: errorDetails
          ? `${errorMessage}: ${errorDetails}`
          : errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Sin renderizar pero montado: el padre puede mostrar otro paso (p. ej. el
  // formulario de tarjeta) y al volver el sub-paso y los datos siguen intactos.
  if (!active) return null;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Checkout Form */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-brown-dark">Checkout</h2>
            <Button variant="outline" onClick={handleBack}>
              {subStep > 0 ? "← Anterior" : "← Volver"}
            </Button>
          </div>

          <div
            className="flex items-center gap-2"
            data-testid="sub-step-dots"
            aria-hidden="true"
          >
            {subSteps.map((key, index) => (
              <span
                key={key}
                data-testid="sub-step-dot"
                data-active={index <= subStep}
                className={`h-2 rounded-full transition-all duration-300 motion-reduce:transition-none ${
                  index <= subStep ? "w-6 bg-primary" : "w-2 bg-gray-300"
                }`}
              />
            ))}
          </div>

          <p
            className="text-sm font-medium text-brown"
            data-testid="sub-step-indicator"
          >
            Paso {subStep + 1} de {subSteps.length} ·{" "}
            {SUB_STEP_LABELS[currentSubStep]}
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Guest Information */}
            {currentSubStep === "contacto" && (
            <Card>
              <CardHeader>
                <CardTitle>Información de contacto</CardTitle>
                <CardDescription>
                  Te enviaremos la confirmación y seguimiento a tu email
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Nombre completo</Label>
                    <Input
                      id="fullName"
                      placeholder="Tu nombre completo"
                      {...register("fullName")}
                      className={errors.fullName ? "border-red-500" : ""}
                    />
                    {errors.fullName && (
                      <p className="text-sm text-red-500">
                        {errors.fullName.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="tu@email.com"
                      {...register("email")}
                      className={errors.email ? "border-red-500" : ""}
                    />
                    {errors.email && (
                      <p className="text-sm text-red-500">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Teléfono</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+507 6000-0000"
                    {...register("phone")}
                    className={errors.phone ? "border-red-500" : ""}
                  />
                  {errors.phone && (
                    <p className="text-sm text-red-500">
                      {errors.phone.message}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
            )}

            {currentSubStep === "envio" && (
              <Card>
                <CardHeader>
                  <CardTitle>Dirección de envío</CardTitle>
                  <CardDescription>
                    Productos físicos en tu carrito requieren dirección de envío
                  </CardDescription>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                    <div className="flex items-start space-x-2">
                      <span className="text-amber-600 text-lg">⚠️</span>
                      <div>
                        <p className="text-amber-800 font-medium text-sm">
                          Zona de entrega limitada
                        </p>
                        <p className="text-amber-700 text-xs">
                          Entregas disponibles únicamente en las provincias de{" "}
                          <strong>Panamá</strong> y{" "}
                          <strong>Panamá Oeste</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="street">Dirección completa</Label>
                    <Input
                      id="street"
                      placeholder="Calle, edificio, casa, apartamento"
                      {...register("street")}
                      className={errors.street ? "border-red-500" : ""}
                    />
                    {errors.street && (
                      <p className="text-sm text-red-500">
                        {errors.street.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="province">Provincia</Label>
                    <Select
                      onValueChange={handleProvinceChange}
                      value={selectedProvince}
                    >
                      <SelectTrigger
                        id="province"
                        className={`${
                          errors.province ? "border-red-500" : ""
                        } bg-white`}
                      >
                        <SelectValue placeholder="Selecciona provincia" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border shadow-lg">
                        {availableProvinces.map((province) => (
                          <SelectItem
                            key={province.value}
                            value={province.value}
                            className="bg-white hover:bg-gray-50 focus:bg-gray-100"
                          >
                            {province.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.province && (
                      <p className="text-sm text-red-500">
                        {errors.province.message}
                      </p>
                    )}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="district">Distrito</Label>
                      <Select
                        onValueChange={handleDistrictChange}
                        value={selectedDistrict}
                        disabled={!selectedProvince}
                      >
                        <SelectTrigger
                          id="district"
                          className={`${
                            errors.district ? "border-red-500" : ""
                          } bg-white ${!selectedProvince ? "opacity-50" : ""}`}
                        >
                          <SelectValue
                            placeholder={
                              selectedProvince
                                ? "Selecciona distrito"
                                : "Primero selecciona provincia"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent className="bg-white border shadow-lg">
                          {availableDistricts.map((district) => (
                            <SelectItem
                              key={district.value}
                              value={district.value}
                              className="bg-white hover:bg-gray-50 focus:bg-gray-100"
                            >
                              {district.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.district && (
                        <p className="text-sm text-red-500">
                          {errors.district.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="corregimiento">Corregimiento</Label>
                      <Select
                        value={watch("corregimiento") || ""}
                        onValueChange={(value) =>
                          setValue("corregimiento", value)
                        }
                        disabled={!selectedDistrict}
                      >
                        <SelectTrigger
                          id="corregimiento"
                          className={`${
                            errors.corregimiento ? "border-red-500" : ""
                          } bg-white ${!selectedDistrict ? "opacity-50" : ""}`}
                        >
                          <SelectValue
                            placeholder={
                              selectedDistrict
                                ? "Selecciona corregimiento"
                                : "Primero selecciona distrito"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent className="bg-white border shadow-lg max-h-60 overflow-y-auto">
                          {availableCorregimientos.map((corregimiento) => (
                            <SelectItem
                              key={corregimiento}
                              value={corregimiento}
                              className="bg-white hover:bg-gray-50 focus:bg-gray-100"
                            >
                              {corregimiento}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.corregimiento && (
                        <p className="text-sm text-red-500">
                          {errors.corregimiento.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="postalCode">Código postal</Label>
                    <Input
                      id="postalCode"
                      placeholder="00000"
                      maxLength={5}
                      {...register("postalCode")}
                      className={errors.postalCode ? "border-red-500" : ""}
                    />
                    {errors.postalCode && (
                      <p className="text-sm text-red-500">
                        {errors.postalCode.message}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {currentSubStep === "contacto" && !hasPhysicalProducts && (
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 text-sm">📧</span>
                    </div>
                    <div>
                      <h3 className="font-medium text-blue-900">
                        Solo productos digitales
                      </h3>
                      <p className="text-sm text-blue-700">
                        Todos tus productos serán enviados por email - no se
                        requiere dirección de envío
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {currentSubStep === "pago-notas" && (
            <>
            {/* Payment Method */}
            <Card>
              <CardHeader>
                <CardTitle>Método de pago</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-brown">Aceptamos:</span>
                  <img src="/payments/visa.svg" alt="Visa" className="h-5" />
                  <img
                    src="/payments/mastercard.svg"
                    alt="Mastercard"
                    className="h-5"
                  />
                  <img src="/payments/yappy.svg" alt="Yappy" className="h-5" />
                </div>
                <div className="grid gap-3">
                  {paymentMethods.map((method) => {
                    const Icon = method.icon;
                    return (
                      <label
                        key={method.value}
                        onClick={() => {
                          if (method.disabled) return;
                          setValue("paymentMethod", method.value, {
                            shouldValidate: true,
                          });
                        }}
                        className={`relative flex items-center space-x-3 p-4 border-2 rounded-lg transition-all ${
                          method.disabled
                            ? "opacity-50 cursor-not-allowed bg-gray-50"
                            : "cursor-pointer"
                        } ${
                          selectedPaymentMethod === method.value &&
                          !method.disabled
                            ? "border-primary bg-primary/5"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <input
                          type="radio"
                          value={method.value}
                          {...register("paymentMethod")}
                          className="sr-only"
                          disabled={method.disabled}
                        />
                        <Icon
                          className={`w-6 h-6 ${
                            method.disabled ? "text-gray-400" : "text-brown"
                          }`}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span
                              className={`font-medium ${
                                method.disabled ? "text-gray-500" : ""
                              }`}
                            >
                              {method.label}
                            </span>
                            {method.recommended && !method.disabled && (
                              <span className="text-xs bg-primary text-brown-dark px-2 py-1 rounded-full">
                                Recomendado
                              </span>
                            )}
                          </div>
                          <p
                            className={`text-sm ${
                              method.disabled
                                ? "text-gray-400"
                                : "text-brown/70"
                            }`}
                          >
                            {method.description}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle>Notas adicionales (opcional)</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Instrucciones especiales para la entrega..."
                  {...register("notes")}
                  rows={3}
                />
              </CardContent>
            </Card>
            </>
            )}

            <div className="flex gap-3">
              {subStep < subSteps.length - 1 ? (
                <Button
                  key="continue"
                  type="button"
                  onClick={(event) => {
                    // La misma posición pasa a ser el botón de envío (`submit`) en el
                    // re-render; sin esto el navegador ejecuta su acción por defecto y
                    // envía la orden saltando la selección de método de pago.
                    event.preventDefault();
                    goToNextSubStep();
                  }}
                  className="flex-1 bg-primary hover:bg-primary-dark text-brown-dark"
                >
                  Continuar a {SUB_STEP_LABELS[subSteps[subStep + 1]]}
                </Button>
              ) : (
                <Button
                  key="submit"
                  type="submit"
                  className="flex-1 bg-primary hover:bg-primary-dark text-brown-dark text-lg py-6"
                  disabled={isLoading}
                >
                  {isLoading && (
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  )}
                  {selectedPaymentMethod === "cubopago"
                    ? `Pasar a datos de pago • USD ${(subtotal || 0).toFixed(2)}`
                    : `Confirmar pedido • USD ${(subtotal || 0).toFixed(2)}`}
                </Button>
              )}
            </div>
          </form>
        </div>

        {/* Order Summary */}
        <div className="lg:sticky lg:top-6 h-fit">
          <Card>
            <CardHeader>
              <CardTitle>Resumen del pedido</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Cart Items */}
              <div className="space-y-3">
                {cartItems.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex-shrink-0">
                      {item.image && (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.name}</p>
                      <p className="text-sm text-brown">
                        Cantidad: {item.quantity}
                      </p>
                      {item.customization?.personalizedName && (
                        <p className="text-xs text-primary font-medium mt-0.5 flex items-center gap-1">
                          <span>⭐</span>
                          <span>
                            Personalizado: "
                            {item.customization.personalizedName}"
                          </span>
                        </p>
                      )}
                      {item.customization?.notes && (
                        <p className="text-xs text-brown/70 mt-0.5 flex items-center gap-1">
                          <span>📝</span>
                          <span>{item.customization.notes}</span>
                        </p>
                      )}
                    </div>
                    <p className="font-medium">
                      USD {(item.subtotal || 0).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>

              <hr />

              {/* Totals */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>USD {(subtotal || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Envío</span>
                  <span className="text-sm text-brown">
                    Se le indicará por WhatsApp
                  </span>
                </div>
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span>USD {(subtotal || 0).toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
